import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Phone, Package } from "lucide-react";
import { api } from "@/lib/api";
import { Money, ListShell } from "@/components/ui-veda/Primitives";

function formatDT(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function Orders() {
    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [consults, setConsults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sp] = useSearchParams();
    const highlight = sp.get("highlight");

    async function load() {
        setLoading(true);
        try {
            const [o, c] = await Promise.all([
                api.get("/orders"),
                api.get("/consultations"),
            ]);
            setOrders(o.data);
            setConsults(c.data);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-8 md:py-12">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={14} /> Home
                    </Link>
                    <h1 className="font-display mt-3 text-4xl text-veda-onSurface md:text-5xl">
                        My activity
                    </h1>
                </div>
            </div>
            <div className="sticky top-16 z-10 border-b border-veda-border bg-veda-surface">
                <div className="veda-container grid max-w-2xl grid-cols-2">
                    <button
                        onClick={() => setTab("orders")}
                        className={`py-3 text-xs font-semibold uppercase tracking-[0.14em] ${
                            tab === "orders"
                                ? "border-b-2 border-veda-brand text-veda-onSurface"
                                : "text-veda-muted"
                        }`}
                    >
                        Orders ({orders.length})
                    </button>
                    <button
                        onClick={() => setTab("consults")}
                        className={`py-3 text-xs font-semibold uppercase tracking-[0.14em] ${
                            tab === "consults"
                                ? "border-b-2 border-veda-brand text-veda-onSurface"
                                : "text-veda-muted"
                        }`}
                    >
                        Consultations ({consults.length})
                    </button>
                </div>
            </div>

            <div className="veda-container-narrow pt-6">
                {renderTabBody({ loading, tab, orders, consults, highlight, formatDT })}
            </div>
        </div>
    );
}

function renderTabBody({ loading, tab, orders, consults, highlight, formatDT }) {
    if (loading) {
        return <ListShell loading />;
    }
    if (tab === "orders") {
        if (orders.length === 0) {
            return <ListShell isEmpty emptyMessage="No orders yet." />;
        }
        return (
            <ul className="divide-y divide-veda-divider">
                {orders.map((o) => (
                    <li
                        key={o.order_id}
                        className={`py-4 ${
                            highlight === o.order_id ? "bg-veda-brand/5" : ""
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="grid h-8 w-8 place-items-center bg-veda-surface2">
                                    <Package size={14} />
                                </span>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                        {o.status} · {o.payment_method}
                                    </p>
                                    <p className="text-sm font-semibold text-veda-onSurface">
                                        #{o.order_id.slice(-8)}
                                    </p>
                                </div>
                            </div>
                            <p className="text-sm font-semibold">
                                <Money value={o.total} />
                            </p>
                        </div>
                        <p className="mt-1 text-xs text-veda-muted">
                            {formatDT(o.created_at)}
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-veda-muted">
                            {o.items.map((it, idx) => (
                                <li key={`${it.product_id || "item"}-${idx}`}>
                                    {it.quantity} × {it.name}
                                </li>
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>
        );
    }
    if (consults.length === 0) {
        return <ListShell isEmpty emptyMessage="No consultations yet." />;
    }
    return (
        <ul className="divide-y divide-veda-divider">
            {consults.map((c) => (
                <li key={c.consultation_id} className="py-4">
                    <Link
                        to={`/consultation/${c.consultation_id}`}
                        className="flex items-center gap-3"
                    >
                        <img
                            src={c.doctor_avatar}
                            alt=""
                            className="h-11 w-11 object-cover"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-veda-onSurface">
                                {c.doctor_name}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                {c.mode === "call" ? (
                                    <>
                                        <Phone size={10} className="mr-1 inline" />{" "}
                                        Call · {c.slot}
                                    </>
                                ) : (
                                    <>
                                        <MessageCircle
                                            size={10}
                                            className="mr-1 inline"
                                        />{" "}
                                        Chat · {c.slot}
                                    </>
                                )}
                            </p>
                        </div>
                        <span className="text-sm">
                            <Money value={c.fee} />
                        </span>
                    </Link>
                </li>
            ))}
        </ul>
    );
}
